import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput, ActivityIndicator } from 'react-native';
import { AlertTriangle, CheckCircle2, X } from '../lib/icons';
import { shadows } from '../lib/styles';

export type PlanOverride = 'assign_honeydone' | 'dispatch' | 'cannot_help';

interface PlanOverrideFormProps {
  currentApproach?: string;
  planData?: {
    approach?: string;
    approach_details?: {
      in_house_work?: boolean;
    };
  };
  onOverride: (override: PlanOverride, reason: string) => Promise<void>;
  disabled?: boolean;
}

export const PlanOverrideForm: React.FC<PlanOverrideFormProps> = ({
  currentApproach,
  planData,
  onOverride,
  disabled = false,
}) => {
  // Determine if current approach is in-house work
  const isInHouse = currentApproach === 'in_house' || 
                    planData?.approach === 'in_house' ||
                    planData?.approach_details?.in_house_work === true;
  const [selectedOverride, setSelectedOverride] = useState<PlanOverride | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedOverride) {
      setError('Please select an override option');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for the override');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onOverride(selectedOverride, reason);
      // Reset form after successful submission
      setSelectedOverride(null);
      setReason('');
    } catch (err: any) {
      setError(err.message || 'Failed to apply override');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedOverride(null);
    setReason('');
    setError(null);
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <AlertTriangle size={16} color="#F59E0B" />
        <Text style={styles.headerText}>
          Override AI Plan
        </Text>
      </View>

      <View style={styles.optionsContainer}>
        {/* Show "Assign to HoneyDone" only if current approach is NOT in_house */}
        {!isInHouse && (
          <Pressable
            style={[
              styles.optionButton,
              selectedOverride === 'assign_honeydone' && styles.optionButtonSelected
            ]}
            onPress={() => setSelectedOverride('assign_honeydone')}
            disabled={disabled || submitting}
          >
            <View style={[
              styles.optionRadio,
              selectedOverride === 'assign_honeydone' && styles.optionRadioSelected
            ]} />
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>Assign to HoneyDone</Text>
              <Text style={styles.optionDescription}>
                Override AI and assign this work to HoneyDone's in-house team
              </Text>
            </View>
          </Pressable>
        )}

        {/* Show "Dispatch to Provider" only if current approach IS in_house */}
        {isInHouse && (
          <Pressable
            style={[
              styles.optionButton,
              selectedOverride === 'dispatch' && styles.optionButtonSelected
            ]}
            onPress={() => setSelectedOverride('dispatch')}
            disabled={disabled || submitting}
          >
            <View style={[
              styles.optionRadio,
              selectedOverride === 'dispatch' && styles.optionRadioSelected
            ]} />
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>Dispatch to Provider</Text>
              <Text style={styles.optionDescription}>
                Override AI recommendation and dispatch this work to external service providers
              </Text>
            </View>
          </Pressable>
        )}

        <Pressable
          style={[
            styles.optionButton,
            selectedOverride === 'cannot_help' && styles.optionButtonSelected
          ]}
          onPress={() => setSelectedOverride('cannot_help')}
          disabled={disabled || submitting}
        >
          <View style={[
            styles.optionRadio,
            selectedOverride === 'cannot_help' && styles.optionRadioSelected
          ]} />
          <View style={styles.optionContent}>
            <Text style={styles.optionTitle}>Cannot Help</Text>
            <Text style={styles.optionDescription}>
              Mark this request as outside of HoneyDone's scope
            </Text>
          </View>
        </Pressable>
      </View>

      {selectedOverride && (
        <>
          <View style={styles.reasonContainer}>
            <Text style={styles.reasonLabel}>
              Reason for Override <Text style={styles.required}>*</Text>
            </Text>
            <TextInput
              style={styles.reasonInput}
              placeholder="Explain why you're overriding the AI's recommendation..."
              value={reason}
              onChangeText={setReason}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              editable={!disabled && !submitting}
            />
          </View>

          {error && (
            <View style={styles.errorContainer}>
              <AlertTriangle size={16} color="#DC2626" />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <View style={styles.actionButtons}>
            <Pressable
              style={[styles.button, styles.cancelButton]}
              onPress={handleCancel}
              disabled={submitting}
            >
              <X size={16} color="#6B7280" />
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>

            <Pressable
              style={[
                styles.button,
                styles.submitButton,
                (!reason.trim() || submitting) && styles.submitButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={!reason.trim() || submitting}
            >
              {submitting ? (
                <>
                  <ActivityIndicator size="small" color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Processing...</Text>
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Apply Override</Text>
                </>
              )}
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#F59E0B',
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#92400E',
  },
  optionsContainer: {
    gap: 12,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#E5E7EB',
  },
  optionButtonSelected: {
    borderColor: '#F59E0B',
    backgroundColor: '#FFFBEB',
  },
  optionRadio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    marginTop: 2,
  },
  optionRadioSelected: {
    borderColor: '#F59E0B',
    backgroundColor: '#F59E0B',
    borderWidth: 6,
  },
  optionContent: {
    flex: 1,
    gap: 4,
  },
  optionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  optionDescription: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
  },
  reasonContainer: {
    gap: 8,
  },
  reasonLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  required: {
    color: '#DC2626',
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCA5A5',
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#DC2626',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderColor: '#D1D5DB',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  submitButton: {
    backgroundColor: '#F59E0B',
    borderColor: '#F59E0B',
  },
  submitButtonDisabled: {
    backgroundColor: '#9CA3AF',
    borderColor: '#9CA3AF',
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
